import request, { checkBlockPopup } from '@/api-v2/request';
import type {
  IAppendPortfolioTreeParams,
  IPortfolioTree,
  IPortfolioTreeDraftParams,
  IPortfolioTreeDraftRequestBody,
  ISetPortfolioTreeParams,
} from '@/types/IPortfolioTree';
import type {
  IPortfolioTreeCorrelationMatrixResponse,
  IPortfolioTreeCorrelationMatrixQuery,
  IPortfolioTreeCorrelationChartFullTreeResultQuery,
  IPortfolioTreeCorrelationChartSingleDatumResultQuery,
  IPortfolioTreeCorrelationResponse,
} from '@/types/IPortfolioTreeCorrelation';
import type { IPortfolioTreeExposureQuery } from '@/types/IPortfolioTreeExposureQuery';
import type { IPortfolioTreeExposureResponse } from '@/types/IPortfolioTreeExposureResponse';
import type { IPortfolioTreeMetricsResponse } from '@/types/IPortfolioTreeMetricsResponse';
import type { IPortfolioTreeRollingTrackResponse } from '@/types/IPortfolioTreeRollingTrack';
import type { IPortfolioTreeTracksQuery } from '@/types/IPortfolioTreeTracksQuery';
import type { IPortfolioTreeTrackResponse } from '@/types/IPortfolioTreeTracksResponse';
import type { IPortfolioTreeWeightsQuery, IPortfolioTreeWeightsResponseItem } from '@/types/IPortfolioTreeWeights';
import type { IFrontierLineResponse, IPortfolioTreeFrontierQuery } from '@/types/query/IFrontierLineQuery';
import type { IPortfolioTreeSnapshotResponse } from '@/types/IPortfolioTreeSnapshot';
import type { IPortfolioTreeBacktestResponse } from '@/types/IPortfolioTreeBacktest';
import type { IPortfolioTreeMstQuery } from '@/types/IPortfolioTreeMst';
import type { IMstResponse } from '@/types/IMstResponse';
import type { ISubmitBasketRequest } from '@/types/ISubmitBasketRequest';
import type {
  IPortfolioTreeComponent,
  PerformanceContributionTableQuery,
  PerformanceContributionRankingTableQuery,
  PerformanceContributionReturnDistributionQuery,
  PortfolioReconciliationTableDataQuery,
  PortfolioReconciliationTrackDataQuery,
} from '@/types/IPortfolioTreeComponent';
import type { IReturnRegressionResponse } from '@/types/IReturnRegressionResponse';
import type { FileSignedUrlDTO } from '@/types/dto/FileSignedUrlDTO';
import { RouteName } from '@/constants/RouteName';
import type { Locales } from '@/constants/Locales';
import { useDefaultPdfOptions } from '@/composables/usePdf';
import { FileService } from '@/api-v2/web/file';
import type { ReturnRegressionPortfolioRequestDTO } from '../active-return/types/ReturnRegressionPortfolioRequestDTO';
import type { PublishPortfolioDTO } from './types/PublishPortfolioDTO';
import type { ExcelExportRequest } from './types/ExcelExportRequest';
import type { FactsheetPdfParams } from '@/types/FactsheetPdfParams';
import type { IKdeResponseDto } from '@/types/IKdeResponseDto';
import type { IPortfolioKdeQuery } from '@/types/IPortfolioKdeQuery';
import type { LinkedPortfoliosDependencyQuery } from '@/types/LinkedPortfoliosDependencyQuery';
import type { IRollingCorrelationQuery, IRollingGenericMetricQuery } from '@/types/IRollingTrackQuery';
import type { IMonthlyChart } from '@/types/IMonthlyChart';
import type { PerformanceContributionDatum, TopBottom10Result } from '../performance-attribution';
import type { LocalPortfolioReferencesResponseDTO } from '../strategies/types/LocalPortfolioReferencesResponseDTO';
import type { PortfolioStatusResponseDTO } from '@/types/PortfolioStatusResponseDTO';
import type { OptimizerStatusResponseDTO } from '@/types/OptimizerStatusResponseDTO';
import type { QisOptionsResponseDTO } from '@/types/QisOptionsResponseDTO';
import type { QisOptionsUpdateRequestDTO } from '@/types/QisOptionsUpdateRequestDTO';
import type { IPortfolioTreePriceWithFxQuery } from '@/types/IPortfolioTreePriceWithFxQuery';
import type { IPortfolioTreePriceWithFxResponse } from '@/types/IPortfolioTreePriceWithFxResponse';
import type { IBasketComponentUserPromptRequestDTO } from '@/types/IBasketComponentUserPromptRequest';
import type { IBasketComponentUserPromptResponseDTO } from '@/types/IBasketComponentUserPromptResponse';

const defaultPdfOptions = useDefaultPdfOptions();

export const getPortfolioTreeBySlug = ({
  slug,
  signal,
}: {
  slug: string;
  signal: AbortSignal;
}): Promise<IPortfolioTree> =>
  request({
    url: `/portfolio-trees/${slug}`,
    method: 'get',
    signal,
  }).then((o): IPortfolioTree => o.data);

/**
 * IMPORTANT NOTE: this ALWAYS returns a *new* portfolio tree draft, NOT the saved draft in the database
 */
export const getPortfolioTreeNewDraft = ({
  originalPortfolioSlug,
  params,
  data,
  signal,
}: {
  originalPortfolioSlug: string;
  params: IPortfolioTreeDraftParams;
  data?: IPortfolioTreeDraftRequestBody;
  signal?: AbortSignal;
}): Promise<IPortfolioTree> =>
  request({
    url: `/portfolio-trees/${originalPortfolioSlug}/draft`,
    method: 'post',
    params,
    data,
    signal,
  }).then((o): IPortfolioTree => o.data);

/**
 * Update the draft
 */
export const updateDraft = (
  slug: string,
  data: ISetPortfolioTreeParams,
  signal?: AbortSignal,
): Promise<IPortfolioTree> =>
  request({
    url: `/portfolio-trees/${slug}`,
    method: 'post',
    data,
    signal,
  }).then((o): IPortfolioTree => o.data);

/**
 * append portfolio to another portfolio
 */
export const appendPortfolioTree = (slug: string, payload: IAppendPortfolioTreeParams): Promise<IPortfolioTree> =>
  request({
    url: `/portfolio-trees/${slug}/append`,
    method: 'post',
    data: payload,
  }).then((o): IPortfolioTree => o.data);

export const createPortfolioTree = (payload: ISetPortfolioTreeParams): Promise<IPortfolioTree> =>
  request({
    url: `/portfolio-trees`,
    method: 'post',
    data: payload,
  }).then((o): IPortfolioTree => o.data);

export const savePortfolioTree = (slug: string): Promise<IPortfolioTree> =>
  request({
    url: `/portfolio-trees/${slug}/save`,
    method: 'post',
  }).then((o): IPortfolioTree => o.data);

/**
 * create a new portfolio from existing portfolio
 */
export const copyPortfolioTree = (slug: string, portfolioRequest: ISetPortfolioTreeParams): Promise<IPortfolioTree> =>
  request({
    url: `/portfolio-trees/${slug}/copy`,
    method: 'post',
    data: portfolioRequest,
  }).then((o) => o.data);

export const deletePortfolioTree = (slug: string): Promise<void> =>
  request({
    url: `/portfolio-trees/${slug}`,
    method: 'delete',
  }).then((o) => o.data);

export const getPortfolioTreeTrack = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IPortfolioTreeTracksQuery;
  signal: AbortSignal;
}): Promise<IPortfolioTreeTrackResponse[]> =>
  request({
    url: `/portfolio-trees/${slug}/tracks`,
    method: 'get',
    params,
    signal,
  }).then((o): IPortfolioTreeTrackResponse[] => o.data);

export const getPortfolioTreeMetrics = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IPortfolioTreeTracksQuery | null;
  signal: AbortSignal;
}): Promise<IPortfolioTreeMetricsResponse[]> =>
  request({
    url: `/portfolio-trees/${slug}/metrics`,
    method: 'post',
    data: params,
    signal,
  }).then((o): IPortfolioTreeMetricsResponse[] => o.data);

export const getPortfolioTreeExposure = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IPortfolioTreeExposureQuery;
  signal: AbortSignal;
}): Promise<IPortfolioTreeExposureResponse> =>
  request({
    url: `/portfolio-trees/${slug}/exposure`,
    method: 'get',
    params,
    signal,
  }).then((o): IPortfolioTreeExposureResponse => o.data);

export const getPortfolioTreeRollingTrack = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IRollingCorrelationQuery | IRollingGenericMetricQuery;
  signal: AbortSignal;
}): Promise<IPortfolioTreeRollingTrackResponse> =>
  request({
    url: `/portfolio-trees/${slug}/rolling-track`,
    method: 'get',
    params,
    signal,
  }).then((o): IPortfolioTreeRollingTrackResponse => o.data);

export const getPortfolioTreeCorrelation = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IPortfolioTreeCorrelationChartSingleDatumResultQuery | IPortfolioTreeCorrelationChartFullTreeResultQuery;
  signal: AbortSignal;
}): Promise<IPortfolioTreeCorrelationResponse> =>
  request({
    url: `/portfolio-trees/${slug}/correlation`,
    method: 'get',
    params,
    signal,
  }).then((o): IPortfolioTreeCorrelationResponse => o.data);

export const getPortfolioTreeCorrelationMatrix = (param: {
  slug: string;
  query: IPortfolioTreeCorrelationMatrixQuery;
  signal: AbortSignal;
}): Promise<IPortfolioTreeCorrelationMatrixResponse> =>
  request({
    url: `/portfolio-trees/${param.slug}/correlation-matrix`,
    method: 'post',
    data: param.query,
    signal: param.signal,
  }).then((o): IPortfolioTreeCorrelationMatrixResponse => o.data);

export const getPortfolioTreeWeights = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IPortfolioTreeWeightsQuery;
  signal: AbortSignal;
}): Promise<IPortfolioTreeWeightsResponseItem> =>
  request({
    url: `/portfolio-trees/${slug}/weight`,
    method: 'post',
    data: params,
    signal,
  }).then((o): IPortfolioTreeWeightsResponseItem => o.data);

export const getPortfolioTreeFrontier = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IPortfolioTreeFrontierQuery;
  signal: AbortSignal;
}): Promise<IFrontierLineResponse> =>
  request({
    url: `/portfolio-trees/${slug}/frontier`,
    method: 'get',
    params,
    signal,
  }).then((o): IFrontierLineResponse => o.data);

export const getPortfolioTreeSnapshot = ({
  slug,
  signal,
}: {
  slug: string;
  signal: AbortSignal;
}): Promise<IPortfolioTreeSnapshotResponse[]> =>
  request({
    url: `/portfolio-trees/${slug}/snapshot`,
    method: 'get',
    signal,
  }).then((o): IPortfolioTreeSnapshotResponse[] => o.data);

export const getPortfolioTreeBasketSnapshot = ({
  slug,
  signal,
}: {
  slug: string;
  signal: AbortSignal;
}): Promise<IPortfolioTreeSnapshotResponse[]> =>
  request({
    url: `/portfolio-trees/${slug}/basket-snapshot`,
    method: 'get',
    signal,
  }).then((o): IPortfolioTreeSnapshotResponse[] => o.data);

export const getPortfolioTreeOptimizerSnapshot = ({
  slug,
  signal,
}: {
  slug: string;
  signal: AbortSignal;
}): Promise<IPortfolioTreeSnapshotResponse[]> =>
  request({
    url: `/portfolio-trees/${slug}/optimizer-snapshot`,
    method: 'get',
    signal,
  }).then((o): IPortfolioTreeSnapshotResponse[] => o.data);

export const getPortfolioTreeBacktest = ({
  slug,
  signal,
}: {
  slug: string;
  signal: AbortSignal;
}): Promise<IPortfolioTreeBacktestResponse[]> =>
  request({
    url: `/portfolio-trees/${slug}/backtest`,
    method: 'get',
    signal,
  }).then((o): IPortfolioTreeBacktestResponse[] => o.data);

export const getPortfolioTreeMst = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IPortfolioTreeMstQuery;
  signal: AbortSignal;
}): Promise<IMstResponse> =>
  request({
    url: `/portfolio-trees/${slug}/mst`,
    method: 'get',
    params,
    signal,
  }).then((o): IMstResponse => o.data);

/**
 * Performance Contribution endpoint
 */
export const getPortfolioConstructionComponent = ({
  slug,
  params,
}: {
  slug: string;
  params: PortfolioReconciliationTableDataQuery | PortfolioReconciliationTrackDataQuery;
}): Promise<IPortfolioTreeComponent> =>
  request({
    url: `/portfolio-trees/${slug}/component`,
    method: 'get',
    params,
  }).then((o): IPortfolioTreeComponent => o.data);

export const getPortfolioContributionTable = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: PerformanceContributionTableQuery;
  signal: AbortSignal;
}): Promise<PerformanceContributionDatum[]> =>
  request({
    url: `/portfolio-trees/${slug}/contribution-table`,
    method: 'get',
    params,
    signal,
  }).then((o): PerformanceContributionDatum[] => o.data);

export const getPortfolioContributionReturnDistribution = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: PerformanceContributionReturnDistributionQuery;
  signal: AbortSignal;
}): Promise<IMonthlyChart> =>
  request({
    url: `/portfolio-trees/${slug}/contribution-chart`,
    method: 'get',
    params,
    signal,
  }).then((o): IMonthlyChart => o.data);

export const getPortfolioContributionRankingTable = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: PerformanceContributionRankingTableQuery;
  signal: AbortSignal;
}): Promise<TopBottom10Result[]> =>
  request({
    url: `/portfolio-trees/${slug}/contribution-ranking`,
    method: 'get',
    params,
    signal,
  }).then((o): TopBottom10Result[] => o.data);

export const getPortfolioTreeReturnRegression = ({
  slug,
  data,
  signal,
}: {
  slug: string;
  data: ReturnRegressionPortfolioRequestDTO;
  signal: AbortSignal;
}): Promise<IReturnRegressionResponse> =>
  request({
    method: 'post',
    url: `/portfolio-trees/${slug}/regression`,
    data,
    signal,
  }).then((o) => o.data);

export const getPortfolioPdf = async (
  slug: string,
  url: string,
  params: FactsheetPdfParams,
  indexName: string,
): Promise<void> => {
  const resp = await request({
    url: `/portfolio-trees/${slug}/pdf/factsheet`,
    method: 'post',
    data: {
      url,
      params,
      options: defaultPdfOptions,
    },
  });
  const dto: FileSignedUrlDTO = resp.data;
  if (!dto.signedURL) return;

  checkBlockPopup();

  await FileService.downloadWithCloudFrontUrl(dto.signedURL, 'pdf', {
    tool: RouteName.FACTSHEET,
    indexName,
    date: params.endDate,
  });
};

export const getPortfolioCombinedModulePdf = async (
  slug: string,
  url: string,
  params: FactsheetPdfParams & {
    locale?: Locales;
  },
  indexName: string,
): Promise<void> => {
  const resp = await request({
    url: `/portfolio-trees/${slug}/pdf/combined-module`,
    method: 'post',
    data: {
      url,
      params,
      options: defaultPdfOptions,
    },
  });
  const dto: FileSignedUrlDTO = resp.data;
  if (!dto.signedURL) return;

  checkBlockPopup();

  await FileService.downloadWithCloudFrontUrl(dto.signedURL, 'pdf', {
    tool: RouteName.PORTFOLIO_COMBINED_MODULE_PDF,
    indexName,
    date: params.endDate,
  });
};

export const getStrategyCombinedModulePdf = async (
  slug: string,
  url: string,
  params: FactsheetPdfParams & {
    locale?: Locales;
  },
  indexName: string,
): Promise<void> => {
  const resp = await request({
    url: `/strategies/${slug}/pdf/combined-module`,
    method: 'post',
    data: {
      url,
      params,
      options: defaultPdfOptions,
    },
  });
  const dto: FileSignedUrlDTO = resp.data;
  if (!dto.signedURL) return;

  checkBlockPopup();

  await FileService.downloadWithCloudFrontUrl(dto.signedURL, 'pdf', {
    tool: RouteName.STRATEGY_COMBINED_MODULE_PDF,
    indexName,
    date: params.endDate,
  });
};

export const getPortfolioExcel = async (
  slug: string,
  query: ExcelExportRequest,
  indexName: string,
  shouldUseAlgoQuant: boolean,
): Promise<void> => {
  const resp = await request({
    url: `/portfolio-trees/${slug}/excel`,
    method: 'POST',
    params: query,
    headers: {
      'Should-Use-AlgoQuant': String(shouldUseAlgoQuant),
    },
  });
  const dto: FileSignedUrlDTO = resp.data;
  if (!dto.signedURL) return;

  checkBlockPopup();

  await FileService.downloadWithCloudFrontUrl(dto.signedURL, 'xlsx', {
    tool: RouteName.FACTSHEET,
    indexName,
  });
};

export const publishPortfoliosToHSBCTeams = (slug: string, payload: PublishPortfolioDTO): Promise<void> =>
  request({
    url: `/portfolio-trees/${slug}/publish`,
    method: 'post',
    data: payload,
  }).then((o) => o.data);

export const getPortfolioTreeKDE = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: IPortfolioKdeQuery;
  signal: AbortSignal;
}): Promise<IKdeResponseDto> =>
  request({
    url: `/portfolio-trees/${slug}/kde`,
    method: 'get',
    params,
    signal,
  }).then((o): IKdeResponseDto => o.data);

export const getLinkedPortfolioDependency = (slug: string, query: LinkedPortfoliosDependencyQuery): Promise<string[]> =>
  request({
    url: `/portfolio-trees/${slug}/linked-portfolios-dependency`,
    method: 'get',
    params: query,
  }).then((o): string[] => o.data);

export const submitBasket = (slug: string, body?: ISubmitBasketRequest): Promise<IPortfolioTree> =>
  request({
    url: `/portfolio-trees/${slug}/basket/submit`,
    method: 'post',
    data: body,
  }).then((o): IPortfolioTree => o.data);

export const getUniqueBasketReferences = (signal: AbortSignal): Promise<LocalPortfolioReferencesResponseDTO> =>
  request({
    url: `/portfolio-trees/basket/references`,
    method: 'get',
    signal,
  }).then((o): LocalPortfolioReferencesResponseDTO => o.data);

export const getPortfolioStatus = (slug: string, signal: AbortSignal): Promise<PortfolioStatusResponseDTO> =>
  request({
    url: `/portfolio-trees/${slug}/status`,
    method: 'get',
    signal,
  }).then((o): PortfolioStatusResponseDTO => o.data);

export const getOptimizerStatus = (slug: string, signal: AbortSignal): Promise<OptimizerStatusResponseDTO> =>
  request({
    url: `/portfolio-trees/${slug}/optimizer-status`,
    method: 'get',
    signal,
  }).then((o): OptimizerStatusResponseDTO => o.data);

export const getQisOptions = (
  draftSlug: string,
  analysisDate: string,
  signal: AbortSignal,
): Promise<QisOptionsResponseDTO> =>
  request({
    url: `/portfolio-trees/${draftSlug}/qis-options`,
    method: 'get',
    params: { analysisDate },
    signal,
  }).then((o): QisOptionsResponseDTO => o.data);

export const updateQisOptions = (draftSlug: string, body: QisOptionsUpdateRequestDTO): Promise<void> =>
  request({
    url: `/portfolio-trees/${draftSlug}/qis-options`,
    method: 'put',
    data: body,
  }).then((o) => o.data);

export const getPortfolioTreePriceWithFxMap = (
  codeOrSlug: string,
  query: IPortfolioTreePriceWithFxQuery,
  signal: AbortSignal,
): Promise<IPortfolioTreePriceWithFxResponse> =>
  request({
    url: `/portfolio-trees/${codeOrSlug}/price-with-fx`,
    method: 'post',
    data: query,
    signal,
  }).then((o): IPortfolioTreePriceWithFxResponse => o.data);

export const getBasketComponentFromUserPrompt = (
  data: IBasketComponentUserPromptRequestDTO,
  signal: AbortSignal,
): Promise<IBasketComponentUserPromptResponseDTO[]> =>
  request({
    url: `/portfolio-trees/basket/parse-user-prompt`,
    method: 'post',
    data,
    signal,
  }).then((o): IBasketComponentUserPromptResponseDTO[] => o.data);
